import { ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { LoadingScreen } from "./loading-screen";
import { NoOrganizationPage } from "@/features/no-organization/no-organization-page";
import { OrgSelection } from "./org-selection";

interface ProtectedRouteProps {
  children: ReactNode;
  allowUnsubscribed?: boolean; // new prop
  allowUnauthenticated?: boolean; // new prop
}

const SHOW_INDICATOR = false; // Set to true to show debug indicators

export function ProtectedRoute({ children, allowUnsubscribed = false, allowUnauthenticated = false }: ProtectedRouteProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    session,
    isPending: isPendingOnboarding,
    activeOrg,
    organizations,
    showOrgSelection,
    selectedOrgId,
    setSelectedOrgId,
    isSettingActive,
    handleSetActiveOrganization,
    onCloseOrgSelection,
  } = useOnboarding("done");
  const { hasValidSubscription, isTrialExpired, isLoading: isLoadingSubscription, error: subscriptionError } = useSubscriptionStatus();
  // Use a ref to avoid re-renders and synchronous setState inside effects
  const hasProcessedSubscriptionRef = useRef(false);
  const [isLoadingStable, setIsLoadingStable] = useState(false);
  const loadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadingStartRef = useRef<number | null>(null);
  const hasRenderedOnceRef = useRef(false);

  const MIN_LOADING_MS = 200;

  // detect onboarding route and choose loader
  const isOnboardingRoute = location.pathname.startsWith("/onboarding");

  // Manage stable loading state to prevent flicker
  useEffect(() => {
    const shouldLoad = isPendingOnboarding || (isLoadingSubscription && !allowUnsubscribed);

    if (loadingTimerRef.current) {
      clearTimeout(loadingTimerRef.current);
      loadingTimerRef.current = null;
    }

    if (shouldLoad) {
      if (loadingStartRef.current === null) {
        loadingStartRef.current = performance.now();
      }
      // Use a microtask to avoid synchronous setState in effect
      queueMicrotask(() => setIsLoadingStable(true));
      return () => {};
    }

    const elapsed = loadingStartRef.current ? performance.now() - loadingStartRef.current : 0;
    const remaining = Math.max(0, MIN_LOADING_MS - elapsed);

    loadingTimerRef.current = setTimeout(() => {
      setIsLoadingStable(false);
      loadingStartRef.current = null;
    }, remaining);

    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    };
  }, [isPendingOnboarding, isLoadingSubscription, allowUnsubscribed]);

  // State to track if onboarding UI has already rendered once (derived from ref for render use)
  const [hasRenderedOnce, setHasRenderedOnce] = useState(false);

  // Track if onboarding UI has already rendered once to avoid brief flashes on refetch
  useEffect(() => {
    if (!isLoadingStable && isOnboardingRoute && !hasRenderedOnceRef.current) {
      hasRenderedOnceRef.current = true;
      // Use a microtask to avoid synchronous setState in effect
      queueMicrotask(() => setHasRenderedOnce(true));
    }
  }, [isLoadingStable, isOnboardingRoute]);

  // Session and subscription indicator colors for debugging
  const sessionIndicatorColor = isPendingOnboarding ? "yellow" : !session ? "red" : "green";
  const subscriptionIndicatorColor = isLoadingSubscription
    ? "yellow"
    : hasValidSubscription === null
      ? "grey"
      : subscriptionError
        ? "red"
        : hasValidSubscription
          ? "green"
          : "red";

  // Debug indicator component
  const indicator = (
    <div style={{ position: "fixed", top: 10, right: 10, zIndex: 9999, display: "flex", gap: "8px" }}>
      <div style={{ background: sessionIndicatorColor }} className="flex-center h-16 w-16 rounded-full text-lg font-bold text-white" title="Session status">
        SES
      </div>
      <div
        style={{ background: subscriptionIndicatorColor }}
        className="flex-center h-16 w-16 rounded-full text-lg font-bold text-white"
        title="Subscription status"
      >
        SUB
      </div>
    </div>
  );

  // Handle authentication flow
  useEffect(() => {
    if (isPendingOnboarding || subscriptionError) {
      return;
    }

    if (!session) {
      if (allowUnauthenticated) {
        return;
      }
      const { pathname } = location;
      const isOnboardingStep = pathname.startsWith("/onboarding/step");
      const isFirstOnboarding = pathname === "/onboarding" || pathname === "/onboarding/step1";

      if (isOnboardingStep && !isFirstOnboarding) {
        navigate("/onboarding", { replace: true });
      } else {
        navigate("/login", { replace: true });
      }
    }
  }, [session, isPendingOnboarding, subscriptionError, allowUnauthenticated, location, navigate]);

  // Handle subscription flow
  useEffect(() => {
    // Skip subscription checks for routes allowing unsubscribed users
    if (allowUnsubscribed || !session || !activeOrg) {
      return;
    }

    // Already on trial-end page
    if (location.pathname === "/subscriptions/trial-end") {
      hasProcessedSubscriptionRef.current = true;
      return;
    }

    // Prevent double processing
    if (hasProcessedSubscriptionRef.current || isLoadingSubscription) {
      return;
    }

    // Check subscription status
    if (hasValidSubscription !== undefined) {
      const invalid = !hasValidSubscription || isTrialExpired;
      if (invalid) {
        navigate("/subscriptions/trial-end", { replace: true });
        return;
      }
      hasProcessedSubscriptionRef.current = true;
    }
  }, [allowUnsubscribed, session, activeOrg, location.pathname, isLoadingSubscription, hasValidSubscription, isTrialExpired, navigate]);

  // reset when user switches org
  useEffect(() => {
    if (activeOrg) {
      hasProcessedSubscriptionRef.current = false;
    }
  }, [activeOrg]);

  // Early returns for different states
  const canBypassLoadingForOnboarding = isOnboardingRoute && hasRenderedOnce;

  if (isLoadingStable && !canBypassLoadingForOnboarding) {
    return (
      <>
        {SHOW_INDICATOR && indicator}
        <LoadingScreen isOnboardingRoute={isOnboardingRoute} />
      </>
    );
  }

  if (!session && !allowUnauthenticated) {
    return null;
  }

  // Aucune organisation trouvée : afficher une page dédiée
  // IMPORTANT: Only show this when we're SURE there are no organizations (not just null activeOrg during loading)
  // Don't show during onboarding flow as the user is in the process of creating an organization
  if (session && !activeOrg && !isPendingOnboarding && (!organizations || organizations.length === 0) && !isOnboardingRoute) {
    return <NoOrganizationPage />;
  }

  // Afficher le dialogue de sélection d'organisation si nécessaire
  // Cela se produit quand l'utilisateur a plusieurs organisations mais aucune active
  if (showOrgSelection && organizations && organizations.length > 1) {
    return (
      <>
        {SHOW_INDICATOR && indicator}
        <LoadingScreen isOnboardingRoute={isOnboardingRoute} />
        <OrgSelection
          open={showOrgSelection}
          organizations={organizations}
          selectedOrgId={selectedOrgId}
          onSelectedOrgChange={setSelectedOrgId}
          onConfirm={() => handleSetActiveOrganization(selectedOrgId)}
          onClose={onCloseOrgSelection}
          isLoading={isSettingActive}
        />
      </>
    );
  }

  // Render children with optional debug indicator
  return (
    <>
      {SHOW_INDICATOR && indicator}
      {children}
    </>
  );
}
